import { TFunction } from 'i18next';
import { HealthSystemDALEnumsWeightType } from '../api/HealthSystemApi';

/**
 * Every weight in the UI is displayed in kilograms, whatever unit the API sent it in and whatever
 * the farm has configured as its weight type. The farm setting is deliberately not consulted for
 * display any more - two farms looking at the same animal must read the same number.
 *
 * Birth weight is the single exception: a newborn piglet weighs ~1.4 kg, so grams read better.
 * Use BIRTH_WEIGHT_DISPLAY_UNIT there.
 */
export const DISPLAY_WEIGHT_UNIT: HealthSystemDALEnumsWeightType = 2;
export const BIRTH_WEIGHT_DISPLAY_UNIT: HealthSystemDALEnumsWeightType = 1;

export function getWeightUnitLabel(unit: HealthSystemDALEnumsWeightType | null | undefined, t?: TFunction): string {
  if (unit === 2) return t ? t('f-kilograms-short') : 'kg';
  return t ? t('f-grams-short') : 'g';
}

export function getWeightUnitOptions(t?: TFunction) {
  return [
    { value: 1 as HealthSystemDALEnumsWeightType, label: t ? t('f-grams-short') : 'g' },
    { value: 2 as HealthSystemDALEnumsWeightType, label: t ? t('f-kilograms-short') : 'kg' }
  ];
}

/**
 * Converts weight value from grams to the target unit (rounded to max 2 decimals)
 * @param value - Weight value in grams
 * @param currentUnit - Current weight unit (1 = Grams, 2 = Kilograms)
 * @param targetUnit - Target unit (1 = Grams, 2 = Kilograms)
 * @returns Converted weight value rounded to 2 decimal places
 */
export function convertWeight(
  value: number | null | undefined,
  currentUnit: HealthSystemDALEnumsWeightType | null | undefined,
  targetUnit: HealthSystemDALEnumsWeightType | null | undefined
): number | null {
  if (value === null || value === undefined) {
    return null;
  }

  if (currentUnit === 2) {
    value = value * 1000;
  }

  // 1 = Grams (no conversion needed)
  // 2 = Kilograms (divide by 1000)
  let convertedValue = value;
  if (targetUnit === 2) {
    convertedValue = value / 1000;
  }

  return Math.round(convertedValue * 100) / 100;
}

/**
 * Converts a value that carries its own unit into kilograms, the single display unit.
 * @param value - Weight value expressed in `currentUnit`
 * @param currentUnit - Unit the value came in (1 = Grams, 2 = Kilograms)
 */
export function toKilograms(
  value: number | null | undefined,
  currentUnit: HealthSystemDALEnumsWeightType | null | undefined
): number | null {
  return convertWeight(value, currentUnit, DISPLAY_WEIGHT_UNIT);
}

/**
 * Formats weight value with unit label (max 2 decimal places)
 * @param value - Weight value in grams
 * @param currentUnit - Current weight unit (1 = Grams, 2 = Kilograms)
 * @param targetUnit - Target unit (1 = Grams, 2 = Kilograms)
 * @param t - Translation function
 * @returns Formatted weight string with unit
 */
export function formatWeight(
  value: number | null | undefined,
  currentUnit: HealthSystemDALEnumsWeightType | null | undefined,
  targetUnit: HealthSystemDALEnumsWeightType | null | undefined,
  t?: TFunction
): string {
  const convertedValue = convertWeight(value, currentUnit, targetUnit);
  if (convertedValue === null) {
    return '-';
  }

  const unit = getWeightUnitLabel(targetUnit, t);
  return `${convertedValue} ${unit}`;
}
